import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { LicenceClass } from './useFlatCurriculum'

export interface LibraryCourse {
  courseId: string
  title: string
  licenceClass: LicenceClass
  pdfAssetId: string | null
  pdfTitle: string | null
  videoAssetId: string | null
  videoTitle: string | null
}

// Read-only browse of every course's current live content, for the instructor Library
// page (and reusable for a future student-facing browse page). Relies on the same
// "teaching role read published" RLS (migration 038) CurriculumAssignmentPanel already
// uses — no new policy needed. Same latest-published-version-per-course dedup as
// useCurriculumAssignments.ts, for the same reason (a "replace" never archives the
// version it supersedes).
export function useCourseLibrary() {
  const [courses, setCourses] = useState<LibraryCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCourses = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: fetchErr } = await supabase
      .from('curriculum_courses')
      .select(`
        id, title, licence_class,
        curriculum_course_versions (
          id, version_number, status,
          curriculum_version_modules (
            id,
            curriculum_version_lessons (
              id,
              curriculum_lesson_assets (id, asset_type, title, status)
            )
          )
        )
      `)
      .eq('status', 'active')
      .order('title', { ascending: true })

    if (fetchErr) { setError('Could not load the course library.'); setLoading(false); return }

    const result: LibraryCourse[] = (data ?? []).map((c: any) => {
      const versions = (c.curriculum_course_versions ?? []) as any[]
      const latest = versions
        .filter(v => v.status === 'published')
        .sort((a, b) => b.version_number - a.version_number)[0]
      if (!latest) return null

      const mod = (latest.curriculum_version_modules ?? [])[0]
      const lesson = (mod?.curriculum_version_lessons ?? [])[0]
      const assets = (lesson?.curriculum_lesson_assets ?? []).filter((a: any) => a.status === 'active')
      const pdf = assets.find((a: any) => a.asset_type === 'pdf')
      const video = assets.find((a: any) => a.asset_type === 'video')

      return {
        courseId: c.id, title: c.title, licenceClass: c.licence_class,
        pdfAssetId: pdf?.id ?? null, pdfTitle: pdf?.title ?? null,
        videoAssetId: video?.id ?? null, videoTitle: video?.title ?? null,
      }
    }).filter((c): c is LibraryCourse => c !== null)

    setCourses(result)
    setLoading(false)
  }, [])

  useEffect(() => { fetchCourses() }, [fetchCourses])

  return { courses, loading, error, refresh: fetchCourses }
}
